import lottie from 'lottie-web';
import { debouncer } from '../utility/utils';

const instanceMap = new WeakMap();
let observerAttached = false;

const getNodeConfig = (node) => {
  const jsonUrl = node?.dataset?.lottieJson;
  const loop = node?.dataset?.lottieLoop !== '0';
  const autoplay = node?.dataset?.lottieAutoplay !== '0';
  const renderer = node?.dataset?.lottieRenderer || 'svg';

  return {
    jsonUrl,
    loop,
    autoplay,
    renderer,
  };
};

const configMatchesLoaded = (node, config) => {
  return (
    node.dataset.lottieLoadedJson === (config.jsonUrl || '') &&
    node.dataset.lottieLoadedLoop === (config.loop ? '1' : '0') &&
    node.dataset.lottieLoadedAutoplay === (config.autoplay ? '1' : '0') &&
    node.dataset.lottieLoadedRenderer === (config.renderer || 'svg')
  );
};

const markLoadedConfig = (node, config) => {
  node.dataset.lottieLoadedJson = config.jsonUrl || '';
  node.dataset.lottieLoadedLoop = config.loop ? '1' : '0';
  node.dataset.lottieLoadedAutoplay = config.autoplay ? '1' : '0';
  node.dataset.lottieLoadedRenderer = config.renderer || 'svg';
};

const destroyNodeAnimation = (node) => {
  const existing = instanceMap.get(node);
  if (existing && typeof existing.destroy === 'function') {
    try {
      existing.destroy();
    } catch (e) {
      // Non-fatal
    }
  }
  instanceMap.delete(node);
  node.dataset.lottieInitialized = '0';
};

const initNode = (node) => {
  if (!node) return;

  const config = getNodeConfig(node);
  if (!config.jsonUrl) return;

  // If already initialized with the same config, do nothing.
  if (node.dataset.lottieInitialized === '1' && configMatchesLoaded(node, config)) return;

  // If config changed (e.g., new upload), tear down and re-init.
  if (node.dataset.lottieInitialized === '1') {
    destroyNodeAnimation(node);
  }

  try {
    const animation = lottie.loadAnimation({
      container: node,
      renderer: config.renderer,
      loop: config.loop,
      autoplay: config.autoplay,
      path: config.jsonUrl,
    });

    instanceMap.set(node, animation);

    // If no aspect ratio is defined in CSS, infer it from the JSON.
    // This prevents 0-height containers in layouts that don't set aspect-ratio.
    animation.addEventListener('DOMLoaded', () => {
      try {
        const computed = window.getComputedStyle(node);
        if (computed && computed.aspectRatio && computed.aspectRatio !== 'auto') return;

        const data = animation.animationData;
        if (!data || !data.w || !data.h) return;

        node.style.aspectRatio = `${data.w} / ${data.h}`;
      } catch (e) {
        // No-op: aspect ratio inference is best-effort.
      }
    });

    node.dataset.lottieInitialized = '1';
    markLoadedConfig(node, config);
  } catch (e) {
    // Keep failures non-fatal; missing/malformed JSON should not break the page.
    console.warn('Failed to initialize lottie animation', e);
  }
};

const isInBlockEditor = () => {
  // Common WP admin/editor markers.
  return (
    document.body?.classList?.contains('block-editor-page') ||
    !!document.querySelector('.block-editor__container, .edit-post-layout')
  );
};

const attachObserverOnce = () => {
  if (observerAttached) return;
  observerAttached = true;

  const debouncedInit = debouncer(() => initializeLottie(), 150);

  const observer = new MutationObserver((mutations) => {
    let shouldReinit = false;

    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        // New nodes added (new block, block duplicated, etc.)
        if (mutation.addedNodes && mutation.addedNodes.length) {
          shouldReinit = true;
          break;
        }
      }

      if (mutation.type === 'attributes') {
        const target = mutation.target;
        if (target && target.matches && target.matches('[data-lottie-json]')) {
          shouldReinit = true;
          break;
        }
      }
    }

    if (shouldReinit) debouncedInit();
  });

  observer.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: [
      'data-lottie-json',
      'data-lottie-loop',
      'data-lottie-autoplay',
      'data-lottie-renderer',
    ],
  });
};

export const initializeLottie = () => {
  const nodes = document.querySelectorAll('[data-lottie-json]');
  if (nodes && nodes.length) {
    nodes.forEach((node) => initNode(node));
  }

  // In the block editor, blocks/media fields update after initial DOM ready.
  // Watch for new nodes + attribute changes so animations initialize when uploaded.
  if (isInBlockEditor()) {
    attachObserverOnce();
  }
};
